/**
 * BOSS Reports — the pre-filter dialog.
 *
 * The web port of the legacy pre-filter editors: the seven-tab date chooser (Day, Week, Month,
 * Season, Year, Custom, Relative) with its prior-year comparison count, the store and supplier
 * include/exclude dual lists with their filter box, and the free-text value list.
 *
 * Two behaviours are reproduced deliberately rather than reinvented:
 *
 * - MYSTORE may not sit alongside a real store in the same list. The server enforces this too
 *   (a filter list is model state, not just UI state), but catching it here means the user finds
 *   out while they are still in the editor instead of after a round trip.
 * - Every tab shows the window it resolves to, half-open, in yyyy-MM-dd. The port fixed three
 *   legacy off-by-one bugs — seasons losing their last second, Custom dropping the last day
 *   picked, and `lastMonth` overlapping the current month — so the numbers will not always match
 *   the old client, and showing the window is what makes that visible instead of mysterious.
 *
 * "Today" is the SERVER's business date, handed down through `today`, never the browser's own.
 * Only the four relative ranges depend on it, but they depend on it completely: a viewer west of
 * the business zone late in the evening would otherwise be shown "Yesterday" as one day while the
 * report ran another. The same date also seeds every blank tab, so a fresh Day filter opens on the
 * chain's today rather than on the laptop's.
 */
import { useMemo, useState } from 'react'
import type {
    BossField,
    BossPreFilterValue,
    BossRelativeRange,
    BossSeasonCode,
    BossStoreOption,
} from '../../types/boss'
import {
    DATE_TABS,
    MY_STORE,
    MY_STORE_CONFLICT,
    MY_STORE_LABEL,
    RELATIVE_RANGES,
    SEASON_CODES,
    clampReoYears,
    defaultsFromFields,
    describePreFilter,
    isIsoDate,
    mondayOfIso,
    preFilterEditor,
    previewWindow,
    relativeLabel,
    todayIso,
    type BossDateTab,
} from '../../lib/bossPreFilters'

export type PreFilterDialogProps = {
    /** The report's pre-filter fields — only `isFiltered && !isOrphaned` are editable. */
    fields: BossField[]
    /** The values the dialog opens with: the URL's, or the report's stored defaults. */
    values: BossPreFilterValue[]
    /** Store options for a store field's dual list. */
    stores: BossStoreOption[]
    /** Supplier names for a supplier field's dual list. */
    suppliers: string[]
    /** True when the caller may write these values back onto the report as its defaults. */
    canSaveDefaults: boolean
    /**
     * The server's business date, `yyyy-MM-dd` — what "Yesterday", "Last Week" and "Last Month"
     * resolve against when the report runs. Comes from `BossReportDetail.businessDate`. It falls
     * back to the browser's date only so the component stays usable in isolation; in the app it is
     * always supplied, because a preview computed against a different date than the run is the bug
     * this prop exists to close.
     */
    today?: string
    /** The zone that date was read in, named in the hint when the viewer's own date differs. */
    businessTimeZone?: string
    onApply: (values: BossPreFilterValue[], saveDefaults: boolean) => void
    onCancel: () => void
}

/** The legacy tab captions, in the legacy order. */
const DATE_TAB_LABELS: Record<BossDateTab, string> = {
    day: 'Day',
    week: 'Week',
    month: 'Month',
    season: 'Season',
    year: 'Year',
    custom: 'Custom',
    relative: 'Relative',
}

const REO_CHOICES = [0, 1, 2, 3, 4, 5] as const

const DUPLICATE_VALUE = 'That value is already in this filter.'

/** How many options a dual list renders before it asks the user to narrow the filter box. */
const MAX_VISIBLE_OPTIONS = 200

/**
 * Every tab keeps its own inputs, so switching from Season back to Custom does not throw away
 * what was typed. Only the active tab contributes a value.
 */
type DateDraft = {
    tab: BossDateTab
    dayFrom: string
    dayTo: string
    weekDay: string
    month: string
    year: string
    seasonCode: BossSeasonCode
    seasonYear: string
    customFrom: string
    customTo: string
    relative: BossRelativeRange
    reo: number
}

type ListDraft = { include: string[]; exclude: string[] }

type Draft = { kind: 'date'; date: DateDraft } | { kind: 'list'; list: ListDraft }

type Option = { id: string; value: string; label: string; note: string | null }

// ── Seeding ───────────────────────────────────────────────────────────

function blankDate(today: string): DateDraft {
    return {
        tab: 'day',
        dayFrom: today,
        dayTo: today,
        weekDay: today,
        month: today.slice(0, 7),
        year: today.slice(0, 4),
        seasonCode: 'SS',
        seasonYear: today.slice(0, 4),
        customFrom: today,
        customTo: today,
        relative: 'yesterday',
        reo: 0,
    }
}

function seedDate(value: BossPreFilterValue | undefined, today: string): DateDraft {
    const base = blankDate(today)
    if (!value || !value.dateType || value.dateType === 'none') return base
    const seeded: DateDraft = { ...base, reo: clampReoYears(value.reoYears) }
    switch (value.dateType) {
        case 'day':
            return { ...seeded, tab: 'day', dayFrom: value.from ?? today, dayTo: value.to ?? value.from ?? today }
        case 'week':
            return { ...seeded, tab: 'week', weekDay: value.from ?? today }
        case 'month':
            return { ...seeded, tab: 'month', month: (value.from ?? today).slice(0, 7) }
        case 'year':
            return { ...seeded, tab: 'year', year: (value.from ?? today).slice(0, 4) }
        case 'season':
            return {
                ...seeded,
                tab: 'season',
                seasonCode: value.seasonCode ?? 'SS',
                seasonYear: typeof value.seasonYear === 'number' ? String(value.seasonYear) : today.slice(0, 4),
            }
        case 'custom':
            return { ...seeded, tab: 'custom', customFrom: value.from ?? today, customTo: value.to ?? today }
        case 'relative':
            return { ...seeded, tab: 'relative', relative: value.relativeRange ?? 'yesterday' }
        default:
            return seeded
    }
}

function seedDrafts(fields: BossField[], values: BossPreFilterValue[], today: string): Record<string, Draft> {
    const byField = new Map(values.map(v => [v.field, v]))
    // Anything the caller did not pass a value for falls back to what the designer persisted on
    // the field, so the editor always opens showing the report's own defaults rather than blank.
    for (const stored of defaultsFromFields(fields)) {
        if (!byField.has(stored.field)) byField.set(stored.field, stored)
    }
    const drafts: Record<string, Draft> = {}
    for (const field of fields) {
        const value = byField.get(field.name)
        if (preFilterEditor(field) === 'date') {
            drafts[field.name] = { kind: 'date', date: seedDate(value, today) }
        } else {
            drafts[field.name] = {
                kind: 'list',
                list: { include: [...(value?.include ?? [])], exclude: [...(value?.exclude ?? [])] },
            }
        }
    }
    return drafts
}

// ── Draft → value ─────────────────────────────────────────────────────

function draftToValue(field: BossField, draft: Draft): BossPreFilterValue | null {
    if (draft.kind === 'list') {
        const { include, exclude } = draft.list
        if (include.length === 0 && exclude.length === 0) return null
        return { field: field.name, include: [...include], exclude: [...exclude] }
    }
    const d = draft.date
    const reoYears = clampReoYears(d.reo)
    switch (d.tab) {
        case 'day':
            if (!isIsoDate(d.dayFrom) || !isIsoDate(d.dayTo)) return null
            return { field: field.name, dateType: 'day', from: d.dayFrom, to: d.dayTo, reoYears }
        case 'week': {
            const monday = mondayOfIso(d.weekDay)
            if (!monday) return null
            return { field: field.name, dateType: 'week', from: monday, reoYears }
        }
        case 'month':
            if (!/^\d{4}-\d{2}$/.test(d.month) || !isIsoDate(`${d.month}-01`)) return null
            return { field: field.name, dateType: 'month', from: `${d.month}-01`, reoYears }
        case 'year':
            if (!/^\d{4}$/.test(d.year)) return null
            return { field: field.name, dateType: 'year', from: `${d.year}-01-01`, reoYears }
        case 'season':
            if (!/^\d{4}$/.test(d.seasonYear)) return null
            return {
                field: field.name,
                dateType: 'season',
                seasonCode: d.seasonCode,
                seasonYear: Number(d.seasonYear),
                reoYears,
            }
        case 'custom':
            if (!isIsoDate(d.customFrom) || !isIsoDate(d.customTo)) return null
            return { field: field.name, dateType: 'custom', from: d.customFrom, to: d.customTo, reoYears }
        case 'relative':
            return { field: field.name, dateType: 'relative', relativeRange: d.relative, reoYears }
        default:
            return null
    }
}

// ── Store values ──────────────────────────────────────────────────────

/**
 * A store filter must carry whatever the predicate's own column holds: a report filtering on
 * STORENUM needs store numbers, one filtering on STORENAME needs names. The legacy engine always
 * emitted store names regardless of the field, which quietly matched nothing on STORENUM reports.
 */
function usesStoreNumber(field: BossField): boolean {
    const column = (field.filterColumn ?? field.name).toUpperCase()
    return column === 'STORENUM' || column === 'STORENUMBER'
}

function storeOptions(field: BossField, stores: BossStoreOption[]): Option[] {
    const byNumber = usesStoreNumber(field)
    const options: Option[] = [{ id: MY_STORE, value: MY_STORE, label: MY_STORE_LABEL, note: null }]
    for (const store of stores) {
        const value = byNumber ? store.storeNumber : store.storeName
        // A store with no number cannot be matched by a STORENUM predicate, so it is not offered.
        if (!value) continue
        options.push({
            id: store.storeId,
            value,
            // "number | name", the legacy row shape. A store with no number shows its name alone
            // rather than borrowing the ** that marks MYSTORE.
            label: store.storeNumber ? `${store.storeNumber} | ${store.storeName}` : store.storeName,
            note: store.isAssigned ? 'yours' : null,
        })
    }
    return options
}

// ── The legacy filter box ─────────────────────────────────────────────

/**
 * The legacy list filter: a query wrapped in double quotes is a contiguous phrase, anything else
 * is a set of words that must all appear in any order. Case-insensitive; an empty query matches
 * everything.
 */
export function matchesFilterQuery(text: string, query: string): boolean {
    const trimmed = query.trim()
    if (trimmed === '') return true
    const haystack = text.toLowerCase()
    if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
        const phrase = trimmed.slice(1, -1).trim().toLowerCase()
        return phrase === '' || haystack.includes(phrase)
    }
    return trimmed
        .toLowerCase()
        .split(/\s+/)
        .filter(word => word !== '')
        .every(word => haystack.includes(word))
}

// ── Component ─────────────────────────────────────────────────────────

export function PreFilterDialog({
    fields,
    values,
    stores,
    suppliers,
    canSaveDefaults,
    today: businessDate,
    businessTimeZone,
    onApply,
    onCancel,
}: PreFilterDialogProps) {
    const editable = useMemo(() => fields.filter(f => f.isFiltered && !f.isOrphaned), [fields])
    // The server's business date when there is one. `isIsoDate` guards it rather than trusting the
    // shape: a malformed value would make every relative preview read "nothing selected", which
    // looks like a broken filter instead of a broken date.
    const today = useMemo(
        () => (isIsoDate(businessDate) ? businessDate! : todayIso()),
        [businessDate])
    // Shown only when it would actually explain something: the viewer's calendar says a different
    // day than the chain's, so "Yesterday" is not the day they would have guessed.
    const viewerDate = useMemo(() => todayIso(), [])
    const businessDateNote = viewerDate === today
        ? null
        : `Today is ${today}${businessTimeZone ? ` in ${businessTimeZone}` : ''}, which is what a relative range is measured from. Your own calendar says ${viewerDate}.`
    const [drafts, setDrafts] = useState<Record<string, Draft>>(() => seedDrafts(editable, values, today))
    const [queries, setQueries] = useState<Record<string, string>>({})
    const [entries, setEntries] = useState<Record<string, string>>({})
    const [errors, setErrors] = useState<Record<string, string>>({})
    // Which rows of a dual list's Available column are ticked, per field. The legacy chooser was a
    // multi-select listbox with Include / Exclude buttons underneath; this is that, in HTML.
    const [selected, setSelected] = useState<Record<string, string[]>>({})

    const setError = (name: string, message: string) =>
        setErrors(prev => ({ ...prev, [name]: message }))

    const patchDate = (name: string, patch: Partial<DateDraft>) =>
        setDrafts(prev => {
            const current = prev[name]
            if (!current || current.kind !== 'date') return prev
            return { ...prev, [name]: { kind: 'date', date: { ...current.date, ...patch } } }
        })

    const patchList = (name: string, next: ListDraft) =>
        setDrafts(prev => {
            const current = prev[name]
            if (!current || current.kind !== 'list') return prev
            return { ...prev, [name]: { kind: 'list', list: next } }
        })

    const toggleSelected = (name: string, value: string) =>
        setSelected(prev => {
            const current = prev[name] ?? []
            return {
                ...prev,
                [name]: current.includes(value) ? current.filter(v => v !== value) : [...current, value],
            }
        })

    /**
     * Moves values into one of the two lists. Validates the whole batch before committing, so a
     * multi-row move either lands completely or not at all — a half-applied MYSTORE conflict would
     * leave a filter that means two things.
     */
    const addValues = (field: BossField, target: 'include' | 'exclude', incoming: string[]) => {
        const draft = drafts[field.name]
        if (!draft || draft.kind !== 'list') return
        const chosen = draft.list[target]
        const other = draft.list[target === 'include' ? 'exclude' : 'include']

        const fresh: string[] = []
        for (const raw of incoming) {
            const value = raw.trim()
            if (value === '') continue
            if (chosen.includes(value) || other.includes(value) || fresh.includes(value)) continue
            fresh.push(value)
        }
        if (fresh.length === 0) {
            setError(field.name, DUPLICATE_VALUE)
            return
        }

        const next = [...chosen, ...fresh]
        // MYSTORE resolves to exactly one store, so pairing it with another in the same list asks
        // for two different answers at once. Include and Exclude are policed independently.
        if (preFilterEditor(field) === 'store' && next.includes(MY_STORE) && next.length > 1) {
            setError(field.name, MY_STORE_CONFLICT)
            return
        }

        patchList(field.name, target === 'include'
            ? { include: next, exclude: other }
            : { include: other, exclude: next })
        setSelected(prev => ({ ...prev, [field.name]: [] }))
        setError(field.name, '')
    }

    const removeValue = (field: BossField, target: 'include' | 'exclude', value: string) => {
        const draft = drafts[field.name]
        if (!draft || draft.kind !== 'list') return
        const kept = draft.list[target].filter(v => v !== value)
        patchList(field.name, target === 'include'
            ? { include: kept, exclude: draft.list.exclude }
            : { include: draft.list.include, exclude: kept })
        setError(field.name, '')
    }

    const apply = (saveDefaults: boolean) => {
        // The dialog refuses the MYSTORE combination as it is built, so this only ever fires on a
        // state that arrived from outside — but a filter that means two things must not be run.
        const conflicts: Record<string, string> = {}
        for (const field of editable) {
            const draft = drafts[field.name]
            if (!draft || draft.kind !== 'list' || preFilterEditor(field) !== 'store') continue
            const bad = (['include', 'exclude'] as const).some(
                key => draft.list[key].includes(MY_STORE) && draft.list[key].length > 1,
            )
            if (bad) conflicts[field.name] = MY_STORE_CONFLICT
        }
        if (Object.keys(conflicts).length > 0) {
            setErrors(prev => ({ ...prev, ...conflicts }))
            return
        }

        const next: BossPreFilterValue[] = []
        for (const field of editable) {
            const draft = drafts[field.name]
            if (!draft) continue
            const value = draftToValue(field, draft)
            if (value) next.push(value)
        }
        onApply(next, saveDefaults)
    }

    const renderDate = (field: BossField, draft: DateDraft) => {
        const value = draftToValue(field, { kind: 'date', date: draft })
        const resolved = value ? previewWindow(value, today) : null
        const monday = mondayOfIso(draft.weekDay)
        return (
            <>
                <div className="boss-date-tabs">
                    {DATE_TABS.map(tab => (
                        <button
                            key={tab}
                            type="button"
                            className={tab === draft.tab ? 'boss-date-tab active' : 'boss-date-tab'}
                            onClick={() => patchDate(field.name, { tab })}
                        >
                            {DATE_TAB_LABELS[tab]}
                        </button>
                    ))}
                </div>

                {/* A grid, not a flex row: .form-input is width:100%, so each control needs a
                    sized box of its own or it collapses to nothing. */}
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                        gap: 'var(--space-3)',
                        alignItems: 'end',
                    }}
                >
                    {draft.tab === 'day' && (
                        <>
                            <label>
                                <span className="form-label">From</span>
                                <input
                                    className="form-input"
                                    type="date"
                                    value={draft.dayFrom}
                                    onChange={e => patchDate(field.name, { dayFrom: e.target.value })}
                                />
                            </label>
                            <label>
                                <span className="form-label">To</span>
                                <input
                                    className="form-input"
                                    type="date"
                                    value={draft.dayTo}
                                    onChange={e => patchDate(field.name, { dayTo: e.target.value })}
                                />
                            </label>
                        </>
                    )}

                    {draft.tab === 'week' && (
                        <label>
                            <span className="form-label">Any day in the week</span>
                            <input
                                className="form-input"
                                type="date"
                                value={draft.weekDay}
                                onChange={e => patchDate(field.name, { weekDay: e.target.value })}
                            />
                        </label>
                    )}

                    {draft.tab === 'month' && (
                        <label>
                            <span className="form-label">Month</span>
                            <input
                                className="form-input"
                                type="month"
                                value={draft.month}
                                onChange={e => patchDate(field.name, { month: e.target.value })}
                            />
                        </label>
                    )}

                    {draft.tab === 'season' && (
                        <>
                            <label>
                                <span className="form-label">Season</span>
                                <select
                                    className="form-select"
                                    value={draft.seasonCode}
                                    onChange={e => patchDate(field.name, { seasonCode: e.target.value as BossSeasonCode })}
                                >
                                    {SEASON_CODES.map(code => (
                                        <option key={code} value={code}>{code}</option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                <span className="form-label">Starting year</span>
                                <input
                                    className="form-input"
                                    type="number"
                                    min={1900}
                                    max={2999}
                                    value={draft.seasonYear}
                                    onChange={e => patchDate(field.name, { seasonYear: e.target.value })}
                                />
                            </label>
                        </>
                    )}

                    {draft.tab === 'year' && (
                        <label>
                            <span className="form-label">Year</span>
                            <input
                                className="form-input"
                                type="number"
                                min={1900}
                                max={2999}
                                value={draft.year}
                                onChange={e => patchDate(field.name, { year: e.target.value })}
                            />
                        </label>
                    )}

                    {draft.tab === 'custom' && (
                        <>
                            <label>
                                <span className="form-label">First day</span>
                                <input
                                    className="form-input"
                                    type="date"
                                    value={draft.customFrom}
                                    onChange={e => patchDate(field.name, { customFrom: e.target.value })}
                                />
                            </label>
                            <label>
                                <span className="form-label">Last day (included)</span>
                                <input
                                    className="form-input"
                                    type="date"
                                    value={draft.customTo}
                                    onChange={e => patchDate(field.name, { customTo: e.target.value })}
                                />
                            </label>
                        </>
                    )}

                    {draft.tab === 'relative' && (
                        <label>
                            <span className="form-label">Range</span>
                            <select
                                className="form-select"
                                value={draft.relative}
                                onChange={e => patchDate(field.name, { relative: e.target.value as BossRelativeRange })}
                            >
                                {RELATIVE_RANGES.map(range => (
                                    <option key={range} value={range}>{relativeLabel(range)}</option>
                                ))}
                            </select>
                        </label>
                    )}

                    <label>
                        <span className="form-label">Prior years</span>
                        <select
                            className="form-select"
                            value={draft.reo}
                            onChange={e => patchDate(field.name, { reo: clampReoYears(Number(e.target.value)) })}
                        >
                            {REO_CHOICES.map(n => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </label>
                </div>

                {draft.tab === 'week' && (
                    <div className="boss-hint">
                        {monday ? `Week of ${monday} (Mon–Sun)` : 'Pick a day to snap to its Monday.'}
                    </div>
                )}

                {/* Only the Relative tab moves with the clock, so this is the only tab where the
                    difference between the chain's day and the viewer's is worth a line. */}
                {draft.tab === 'relative' && businessDateNote && (
                    <div className="boss-hint">{businessDateNote}</div>
                )}

                {/* The window the server will actually run, half-open. Shown on every tab because
                    the port corrects three legacy off-by-ones (§5.5) and the numbers must not
                    look like a mystery to anyone comparing against BOSS. */}
                <div className="boss-hint">
                    {value && resolved
                        ? `${describePreFilter(field, value)} — runs ${resolved.start} → ${resolved.end} (end exclusive)`
                        : 'Nothing selected yet — this field will not narrow the report.'}
                </div>
            </>
        )
    }

    const renderList = (field: BossField, draft: ListDraft) => {
        const kind = preFilterEditor(field)
        const query = queries[field.name] ?? ''
        const entry = entries[field.name] ?? ''
        const ticked = selected[field.name] ?? []
        const taken = new Set([...draft.include, ...draft.exclude])

        const options: Option[] =
            kind === 'store'
                ? storeOptions(field, stores)
                : kind === 'supplier'
                    ? suppliers.map(name => ({ id: name, value: name, label: name, note: null }))
                    : []

        const available = options.filter(o => !taken.has(o.value) && matchesFilterQuery(o.label, query))
        // A supplier list runs to thousands of rows; rendering them all makes the dialog crawl,
        // and the filter box is right there.
        const shown = available.slice(0, MAX_VISIBLE_OPTIONS)
        const labelOf = (value: string) => options.find(o => o.value === value)?.label ?? value

        const chosenColumn = (target: 'include' | 'exclude') => {
            const chosen = target === 'include' ? draft.include : draft.exclude
            return (
                <>
                    <div className="boss-dual-head">
                        <span>{target === 'include' ? 'Include' : 'Exclude'}</span>
                        <span>{chosen.length}</span>
                    </div>
                    <div className="boss-dual-list">
                        {chosen.length === 0 ? (
                            <div className="boss-hint">
                                {target === 'include' ? 'Everything' : 'Nothing'}
                            </div>
                        ) : (
                            chosen.map(value => (
                                <span
                                    key={value}
                                    className={target === 'include' ? 'boss-chip' : 'boss-chip warn'}
                                >
                                    {labelOf(value)}
                                    <button
                                        type="button"
                                        className="boss-icon-btn"
                                        title={`Remove ${labelOf(value)}`}
                                        onClick={() => removeValue(field, target, value)}
                                    >
                                        ✕
                                    </button>
                                </span>
                            ))
                        )}
                    </div>
                </>
            )
        }

        // Free text has no source list to pick from, so the two chosen lists take a column each.
        if (kind === 'value') {
            return (
                <>
                    <div className="boss-filter-box">
                        <input
                            className="form-input"
                            placeholder="Type a value"
                            value={entry}
                            onChange={e => setEntries(prev => ({ ...prev, [field.name]: e.target.value }))}
                            onKeyDown={e => {
                                if (e.key !== 'Enter') return
                                e.preventDefault()
                                addValues(field, 'include', [entry])
                                setEntries(prev => ({ ...prev, [field.name]: '' }))
                            }}
                        />
                    </div>
                    <div className="boss-dual-actions">
                        <button
                            type="button"
                            className="btn btn-secondary boss-btn-sm"
                            disabled={entry.trim() === ''}
                            onClick={() => {
                                addValues(field, 'include', [entry])
                                setEntries(prev => ({ ...prev, [field.name]: '' }))
                            }}
                        >
                            Add to include
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary boss-btn-sm"
                            disabled={entry.trim() === ''}
                            onClick={() => {
                                addValues(field, 'exclude', [entry])
                                setEntries(prev => ({ ...prev, [field.name]: '' }))
                            }}
                        >
                            Add to exclude
                        </button>
                    </div>
                    <div className="boss-dual">
                        <div className="boss-dual-col">{chosenColumn('include')}</div>
                        <div className="boss-dual-col">{chosenColumn('exclude')}</div>
                    </div>
                </>
            )
        }

        return (
            <>
                <div className="boss-filter-box">
                    <input
                        className="form-input"
                        placeholder={'Filter — words in any order, or "an exact phrase"'}
                        value={query}
                        onChange={e => setQueries(prev => ({ ...prev, [field.name]: e.target.value }))}
                    />
                </div>
                <div className="boss-dual">
                    <div className="boss-dual-col">
                        <div className="boss-dual-head">
                            <span>Available</span>
                            <span>{available.length}</span>
                        </div>
                        <div className="boss-dual-list">
                            {shown.length === 0 ? (
                                <div className="boss-hint">Nothing matches that filter.</div>
                            ) : (
                                shown.map(option => (
                                    <label key={option.id} className="boss-dual-item">
                                        <input
                                            type="checkbox"
                                            checked={ticked.includes(option.value)}
                                            onChange={() => toggleSelected(field.name, option.value)}
                                        />
                                        <span>{option.label}</span>
                                        {option.note && <span className="boss-hint">{option.note}</span>}
                                    </label>
                                ))
                            )}
                            {available.length > shown.length && (
                                <div className="boss-hint">
                                    {available.length - shown.length} more — narrow the filter to see them.
                                </div>
                            )}
                        </div>
                        <div className="boss-dual-actions">
                            <button
                                type="button"
                                className="btn btn-secondary boss-btn-sm"
                                disabled={ticked.length === 0}
                                onClick={() => addValues(field, 'include', ticked)}
                            >
                                Include
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary boss-btn-sm"
                                disabled={ticked.length === 0}
                                onClick={() => addValues(field, 'exclude', ticked)}
                            >
                                Exclude
                            </button>
                        </div>
                    </div>
                    <div className="boss-dual-col">
                        {chosenColumn('include')}
                        <div className="boss-panel-foot">{chosenColumn('exclude')}</div>
                    </div>
                </div>
            </>
        )
    }

    // Dimming modal: deliberately no backdrop onClick — dialogs must not close on outside-click.
    return (
        <div className="modal-backdrop">
            <div className="modal" style={{ maxWidth: 820 }}>
                <div className="modal-header">
                    <h2>Pre-filters</h2>
                    <button className="modal-close" onClick={onCancel}>×</button>
                </div>
                <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                    {editable.length === 0 && (
                        <div className="boss-empty">This report has no pre-filters to set.</div>
                    )}
                    {editable.map(field => {
                        const draft = drafts[field.name]
                        if (!draft) return null
                        const error = errors[field.name] ?? ''
                        return (
                            <section key={field.name} className="boss-field-section">
                                <div className="boss-field-title">
                                    <span>{field.caption || field.name}</span>
                                    {field.prompt && <span className="boss-hint">asks every time</span>}
                                </div>
                                {draft.kind === 'date' ? renderDate(field, draft.date) : renderList(field, draft.list)}
                                {error !== '' && <div className="boss-error">{error}</div>}
                            </section>
                        )
                    })}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
                    {canSaveDefaults && (
                        <button className="btn btn-secondary" onClick={() => apply(true)}>
                            Apply & save as defaults
                        </button>
                    )}
                    <button className="btn btn-primary" onClick={() => apply(false)}>Apply</button>
                </div>
            </div>
        </div>
    )
}

export default PreFilterDialog
